import { Table, Tag } from 'antd'
import { ColumnsType } from 'antd/es/table'
import React, { FunctionComponent, useCallback, useMemo } from 'react'

import { FINANCING_SETTINGS } from '../../utils/buybackConst'
import { Money } from '../Money/Money'

export interface FinancingScenario {
  month: number
  loanBalance: number
  loanAtSaleBalance: number
  totalRentCollected: number
  propertyValue: number
  salePrice: number
  buybackPrice: number
  netProceedsMarket: number
  netProceedsBuyback: number
  isSafeMarket: boolean
  isSafeBuyback: boolean
  buybackBalance: number
  marketBalance: number
}

export interface BuybackFormFinancingTableProps {
  calculations: {
    scenarios: FinancingScenario[]
    loanAmount: number
    rentDurationMonths: number
    saleDurationMonths: number
  }
}

const monthRenderer = (month: number) => {
  const className = FINANCING_SETTINGS.buybackBreakpoints.includes(month)
    ? 'font-semibold border-l-2 border-primary'
    : ''
  return <div className={className}>{month}</div>
}

const balanceRenderer = (balance: number) => (
  <Tag
    color={
      balance > FINANCING_SETTINGS.balanceWarningLevel
        ? 'success'
        : balance > 0
        ? 'warning'
        : 'error'
    }
    className={
      balance > FINANCING_SETTINGS.balanceWarningLevel
        ? '!text-lime-600 !mr-0'
        : '!mr-0'
    }
  >
    <Money symbol="Kč" decimalNumbers={0} amount={balance} noData="---" />
  </Tag>
)

const priceRenderer = (amount: number) => (
  <Money
    symbol="Kč"
    grayscale
    decimalNumbers={0}
    amount={amount}
    noData="---"
  />
)

const scroll = { y: 500, x: 'max-content' }

export const BuybackFormFinancingTable: FunctionComponent<BuybackFormFinancingTableProps> =
  ({ calculations }) => {
    const { scenarios, loanAmount, rentDurationMonths, saleDurationMonths } =
      calculations

    const onRowClassName = useCallback(
      (record: FinancingScenario) =>
        record.month === rentDurationMonths
          ? { className: '!bg-blue-50 group-hover:!bg-gray-50' }
          : {},
      [rentDurationMonths]
    )

    const columns = useMemo<ColumnsType<FinancingScenario>>(
      () => [
        {
          title: 'Měs.',
          dataIndex: 'month',
          width: 50,
          align: 'center',
          fixed: 'left',
          render: monthRenderer,
          onCell: onRowClassName,
        },
        {
          title: 'Nemovitost',
          className: 'border-r border-gray-300',
          children: [
            {
              title: 'Výkupní cena',
              key: 'loanAmount',
              className: 'whitespace-nowrap !pl-4 !text-right w-28',
              render: () => priceRenderer(loanAmount),
            },
            {
              title: 'Tržní hodnota',
              dataIndex: 'propertyValue',
              key: 'propertyValue',
              className:
                'whitespace-nowrap !pr-4 border-r border-gray-300 !text-right w-28',
              render: priceRenderer,
            },
          ],
        },
        {
          title: 'Úroky investora',
          className: 'border-r border-gray-300',
          children: [
            {
              title: 'Aktuální',
              key: 'interestAccrued',
              className: 'whitespace-nowrap !pl-4 !text-right w-24',
              render: (_, record) =>
                priceRenderer(record.loanBalance - loanAmount),
            },
            {
              title: 'Při prodeji',
              key: 'interestAccruedAtSale',
              className: `${
                !saleDurationMonths ? 'text-gray-400' : ''
              } whitespace-nowrap !pr-4 !text-right w-28`,
              render: (_, record) =>
                priceRenderer(record.loanAtSaleBalance - loanAmount),
            },
          ],
        },
        {
          title: 'Ke splacení investorovi',
          className: 'border-r border-gray-300',
          children: [
            {
              title: 'Aktuální',
              dataIndex: 'loanBalance',
              className: 'whitespace-nowrap !pl-4 !text-right w-24',
              render: priceRenderer,
            },
            {
              title: 'Při prodeji',
              dataIndex: 'loanAtSaleBalance',
              className: `${
                !saleDurationMonths ? 'text-gray-400' : ''
              } whitespace-nowrap border-r border-gray-300 !pr-4 !text-right w-28`,
              render: priceRenderer,
            },
          ],
        },
        {
          title: 'Inkasovaný příjem',
          className: 'border-r border-gray-300',
          children: [
            {
              title: 'Nájemné',
              dataIndex: 'totalRentCollected',
              className: 'whitespace-nowrap !pl-4 !text-right w-24',
              render: priceRenderer,
            },
            {
              title: 'Tržní prodej',
              dataIndex: 'salePrice',
              className: 'whitespace-nowrap !text-right w-28',
              render: priceRenderer,
            },
            {
              title: 'Zpětný odkup',
              dataIndex: 'buybackPrice',
              className: 'whitespace-nowrap !text-right w-28',
              render: priceRenderer,
            },
          ],
        },
        {
          title: 'Bilance zisků a ztrát',
          children: [
            {
              title: 'Tržní prodej',
              dataIndex: 'marketBalance',
              className: 'whitespace-nowrap !text-right !pl-4 w-28',
              fixed: 'right',
              render: balanceRenderer,
              onCell: onRowClassName,
            },
            {
              title: 'Zpětný odkup',
              dataIndex: 'buybackBalance',
              className: 'whitespace-nowrap !text-right w-28',
              fixed: 'right',
              render: balanceRenderer,
              onCell: onRowClassName,
            },
          ],
        },
      ],
      [loanAmount, onRowClassName, saleDurationMonths]
    )

    const rowClassName = useCallback(
      (record: FinancingScenario) =>
        record.month === rentDurationMonths
          ? 'bg-blue-50 font-semibold group'
          : '',
      [rentDurationMonths]
    )

    return (
      <Table<FinancingScenario>
        columns={columns}
        dataSource={scenarios}
        rowKey="month"
        size="small"
        className="my-8"
        scroll={scroll}
        pagination={false}
        rowClassName={rowClassName}
      />
    )
  }
